import {ActivatedRoute, Route, Router, Routes} from '@angular/router';
import {BreadcrumbService} from 'xng-breadcrumb';
import {Injectable, InjectionToken, Injector, Type, createNgModuleRef, inject} from '@angular/core';
import {get, isEmpty, omit, uniq, orderBy, isFunction, uniqBy, keys, set, cloneDeep, isArray, flattenDeep, negate, isNil, pick, last} from 'lodash';
import {asapScheduler, isObservable, Observable, of, Subject} from 'rxjs';
import { LocationStrategy } from '@angular/common';
import {chain} from 'lodash';
import { takeUntil} from "rxjs/operators";
import { SearchFiltersService } from '../search-filters.service';
import { MakeCallService } from '../makecall.service';
import { LazyLoadingCacheService } from '../lazy-loading-cache.service';
import { dataTranslation } from '../../pipes/translation-format.pipe';
import { PageNotFoundComponent } from '../../components/page-not-found/page-not-found.component';
import { LazyLoadedComponent } from '../../components/lazy-loaded/lazy-loaded.component';
import { TranslationService } from '../translation.service';
import { CommonTokenStorage } from '../common-token-storage.service';
import { CommonGeneralConfigService } from '../common-general-config.service';


export interface NavigationInfo {
  stateData?: any;
  stateKeys?: string | string[];
  mergeQueryParams?: boolean;
  absoluteRoute?: boolean;
  relativeRoute?: boolean;
}


export interface ParentRoutes {
  moduleName?: string;
  menues: string[];
}

export const PARENT_ROUTES = new InjectionToken<ParentRoutes | ParentRoutes[]>('PARENT_ROUTES');

@Injectable({
  providedIn: 'root'
})
export class RouteService {
  menuClosed = false;

  protected breadcrumbService: BreadcrumbService = inject(BreadcrumbService);
  protected router: Router = inject(Router);
  protected injector: Injector = inject(Injector);
  protected lazyLoadingCacheService: LazyLoadingCacheService = inject(LazyLoadingCacheService);
  protected location: LocationStrategy = inject(LocationStrategy);
  protected tokenStorage: CommonTokenStorage = inject(CommonTokenStorage);
  protected searchFiltersService: SearchFiltersService = inject(SearchFiltersService);
  protected makeCall: MakeCallService = inject(MakeCallService);


  constructor() {
    this.initializeStateFromHistory();
    this.location.onPopState((event) => {
      if (!this.disableDynamicRouting) {
        const url = location.pathname;
        const params = location.search;
        if (this.doesRoutePathExist(this.router.config, url)) {
          this.handleOnReloadWithRoute(url + params, true);
        } else {
           document.body.innerHTML = '';
           location.href = url + params;
        }
    }
    });
  }

  doesRoutePathExist(routes: Routes, targetPath: string, idx = 0): boolean {
    const targeSegments = targetPath.split('/');
    if (targeSegments[0] === '') {
      targeSegments.shift();
    } 
    for (const route of routes) {
      // Check current route's path
      if (route.path === targeSegments[idx]) {
        if (idx === (targeSegments.length - 1)) {
          return true;
        } else if (route.children) {
          return this.doesRoutePathExist(route.children, targetPath, idx + 1)
        }
      } else if (route.path == '' && route.children) {
        return this.doesRoutePathExist(route.children, targetPath, idx)
      }
    }
    
    return false;
  }

  states: { [key: string]: any } = {};
  parentRoutesByModule: any = {};

  preservedStateKeys = ['routesState'];
  


  routesHistory = [];
  routeChildren = null;
  paramsByRoute = {};

  allPreDefinedRouteConfigs = [];

  breadcrumbsStorage = [];

  persistParams = [];

  public afterLogin$ = new Subject<any>();

  initiatedRoutes = false;

  finalRoutes: any[] = [];

  allAvailableRoutes: any[] = [];

  allUnhandledRoutes: any[] = [];

  routesByParentId: any = {};

  protected beforeBack: (() => Observable<boolean> | boolean)[] = [];
  protected isBrowserBackBtnClicked = false;
  protected translationService: TranslationService = inject(TranslationService);
  protected route: ActivatedRoute = inject(ActivatedRoute);

  initialize(
    availableComponents: any[],
    preDefinedRouteConfigs: any[] = [],
    unhandledRoutes: string[] = []
  ): void {
    this.allAvailableRoutes = availableComponents;
    this.allPreDefinedRouteConfigs = preDefinedRouteConfigs;
    this.allUnhandledRoutes = unhandledRoutes;
    this.initConfigFromNAvigationHistory();
  }

  removeTrailingSlash(str) {
    if (str?.length > 1 && str.endsWith('/')) {
      return str.slice(0, -1);
    }
    return str;
  }

  getCurrentMenu() {
    let currentMenu;
    let lastPartOfUrl = (this.removeTrailingSlash(this.router.url)).split('/').pop();
    if (lastPartOfUrl.includes('?')) {
      lastPartOfUrl = lastPartOfUrl.substring(0, lastPartOfUrl.indexOf('?'));
    }
    this.menu.find(item => {
      if(item?.menu_url === lastPartOfUrl) {
        currentMenu = item;
      }
    });
    return currentMenu;
  }

  getUrl(route): string {
    const path = route.menu_url.replace('#/', '').replace('.html', '');
    if (this.getComponentInfo(route)) {
      return path == 'javascript;;' ? route.state_name : path;
    } else {
      return path == 'javascript;;'
        ? route.title.replace(' ', '-').toLowerCase()
        : path;
    }
  }

  getChildren(item): any[] {
    return (this.menu || []).filter(subItem => subItem.menu_parent_id === item.id);
  }

  initMenu(type, parent = null): any[] {
    let menu = [];
    if (!parent) {
      menu = (this.menu || []).filter(item => ((item.section === type && !item.menu_parent_id) && (((item?.main_controller)?.trim() !== '') || (((item?.main_controller)?.trim() === '') && this.getChildren(item)?.length))));
    } else {
      menu = this.getChildren(parent).filter(item => item.section === type);
    }
    if (!menu.length) {
      return [];
    }
    menu.forEach(item => {
      dataTranslation.menu.push({
        title: item.title,
        translations: item.translations,
      });
    });
    return orderBy(menu.map(item => {
      const children = this.initMenu(type, item);
      return {
        title: item.title,
        translatedTitle: this.translationService.getDataFromTranslations(item, 'title'),
        icon: item.class_name,
        icon_path: item.icon_path.trim(),
        path: '/' + this.getUrl(item),
        isChild: !!parent,
        order: item.menu_order,
        main_controller: item.main_controller,
        menu_url: item.menu_url,
        type: item.type,
        submenu: children.length ? children : null,
        ...(
          (item?.main_controller === 'open_in_new_tab') ?
            {open_in_new_tab: item?.menu_extension_details?.open_in_new_tab, new_tab_url: item?.menu_extension_details?.url} :
            null
        )
      };
    }), ['order'], ['asc']);
  }

  recursiveMerge(oldRoute, newRoute): Route {
    if (newRoute.component === oldRoute.component && newRoute.path === oldRoute.path) {
      if (newRoute.children) {
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < newRoute.children.length; i++) {
          const idx = (oldRoute.children || []).findIndex((route: Route) => route.path === newRoute.children[i].path);
          if (idx === -1) {
            oldRoute.children.push(newRoute.children[i]);
          } else {
            oldRoute.children[idx] = this.recursiveMerge(oldRoute.children[idx], newRoute.children[i]);
          }
        }
      }
      return oldRoute;
    } else {
      return newRoute;
    }
  }

  mergeChildren(currentChildren: Routes): Routes {
    const newRoute: Route = this.calculateRouteFromHistory();
    const oldRouteIdx: number = currentChildren.findIndex((route: Route) => route.path === newRoute.path);

    if (oldRouteIdx === -1) {
      return currentChildren.concat([newRoute]);
    } else {
      currentChildren[oldRouteIdx] = this.recursiveMerge(currentChildren[oldRouteIdx], newRoute);
      return currentChildren;
    }
  }

  getCurrentPath() {
    return location.pathname + location.search;
  }

  handleOnReload(): void {
    if (this.disableDynamicRouting) {
      return;
    }
    const url = this.getCurrentPath();
    this.handleOnReloadWithRoute(url);
    this.breadcrumbService.breadcrumbs$.subscribe((value) => {
      this.breadcrumbsStorage = value;
      set(this.states, 'routesState.breadcrumbsStorage', this.breadcrumbsStorage);
    });
    asapScheduler.schedule(() => {
        this.router.navigateByUrl(url, {
          info: {
            stateKeys: keys(this.states)
          }
        });
    });
   
  }

  handleOnReloadWithRoute(url: string, fillLastSegement = false): void {
    const fullLocationPath = url.split('?');
    const locationPath = this.removeTrailingSlash(fullLocationPath[0]).split('/');
    const previouseRoutesHistory = cloneDeep(this.routesHistory);
    const parentRouteIdx = previouseRoutesHistory.findIndex(route => this.parentRoutes.includes(route))

    this.routesHistory = url
      ? locationPath.filter(
        (s, idx) => s && (s === locationPath[locationPath.length - 1] ? fillLastSegement : true)
      )
      : [];
    if (window['prefix']) {
      this.routesHistory.shift();
    }

    if (!this.routesHistory.includes(previouseRoutesHistory[parentRouteIdx])){
      this.routesHistory =  previouseRoutesHistory.slice(0, parentRouteIdx + 1).concat(this.routesHistory);
    }
  }

  getFirstMenu(){
    let menuOrdered = chain(this.menu).filter( i => ((i.section == 'left_menu' || i.section == 'top_left_menu') && !i.menu_parent_id)).orderBy('menu_order').map( i => {
      const children =  chain(this.menu).filter(j => j.menu_parent_id == i.id).orderBy('menu_order').value();
      return children.length ? children : [i];
    }).flatten().value();
    return menuOrdered.length ? menuOrdered[0].menu_url : 'home';
  }

  getRoute(url: string, config?: {
    absoluteRoute?: boolean,
    relativeRoute?: boolean
  }): {
    navigationUrl: string;
    queryParams: any;
  } {
    // in absolute case checl prefix and parents
    if (this.isBrowserBackBtnClicked || config?.absoluteRoute) {
      if (this.isBrowserBackBtnClicked) {
        this.isBrowserBackBtnClicked = false;
      }
      if (window['prefix'] && url.indexOf(window['prefix']) === -1) {
        url = '/' + `${window['prefix']}` + (!url.startsWith('/') ? '/' : '') + url;
      }
      this.handleOnReloadWithRoute(url);
      const fullLocationPath = url.split('?');
      return {
        navigationUrl: url,
        queryParams: this.getQueryParamsFromQueryString(fullLocationPath[1])
      }
    } else {
      return this.getMenuItemRoute(url, config?.relativeRoute || false);
    }
  }

  /**
 * @deprecated Stop using it.
 */
  handleRoute(url: string) {
    console.warn(
      "DEPRECATED: Stop usting \"handleRoute\" method, since it is not needed any more"
    );
  }

  getMenuItemRoute(url: string, relativeRoute: boolean = false): {
    navigationUrl: string;
    queryParams: any;
  } {
    const fullLocationPath = url.split('?');
    fullLocationPath[0] = this.removeTrailingSlash(fullLocationPath[0]);
    let params = (get(fullLocationPath, '1') || '')
      .split('&')
      .filter((p) => p.indexOf('per--') === -1)
      .filter((p) => !!p);
    fullLocationPath[1] = fullLocationPath[1] || '';

    const keep = params.find(p => p.indexOf('--keep=true') !== -1);
    params = params.filter(p => p.indexOf('--keep=true') === -1);


    const persistParams = (get(fullLocationPath, '1') || '')
      .split('&')
      .filter((p) => p.indexOf('per--') !== -1)
      .map((p) => p.substr(5));
    let routesParts = fullLocationPath[0].split('/');
    if (window['prefix']) {
      routesParts = (routesParts || []).filter(r => window['prefix'] !== r);
    }
    if (routesParts.length > 1 && !routesParts[0]) {
      routesParts.shift();
    }

    if (routesParts[routesParts.length - 1] === 'login') {
      let url = fullLocationPath.join('?');
      if (window['prefix'] && fullLocationPath[0].indexOf(window['prefix']) === -1) {
        url = '/' + `${window['prefix']}` + url;
      }
      return {
        navigationUrl: url,
        queryParams: this.getQueryParamsFromQueryString(fullLocationPath[1])
      };
    }

    if (
      this.allUnhandledRoutes.find(
        (unhandledRoute) => fullLocationPath[0].indexOf(unhandledRoute) !== -1
      )
    ) {
      let url = fullLocationPath.join('?');
      if (window['prefix'] && !(fullLocationPath[0].startsWith(window['prefix']) || fullLocationPath[0].startsWith('/' + window['prefix']))) {
        url = '/' + `${window['prefix']}` + url;
      }
      return {
        navigationUrl: url,
        queryParams: this.getQueryParamsFromQueryString(fullLocationPath[1])
      };
    }


    if (routesParts[routesParts.length - 1] === '' || routesParts[routesParts.length - 1] === 'home') {
      let defaultState = null;
      let finalise = new Subject();

      this.tokenStorage.getConfiguration().pipe(takeUntil(finalise)).subscribe(res => {
        if(res){  
          defaultState= get(res,'default_state');
          finalise.next(null);
          finalise.complete();
        }
      });

      let defaultStateWithoutSlashes = defaultState?.replace(/\//g, '');
      if (this.menu?.find(r => r.menu_url === 'home') || this.allPreDefinedRouteConfigs?.find(r => r.path === 'home')) {
        routesParts[routesParts.length - 1] = 'home';
      } else if (this.menu.find(r => r.menu_url === defaultStateWithoutSlashes) || this.allPreDefinedRouteConfigs.find(r => r.path === defaultStateWithoutSlashes))
        routesParts[routesParts.length - 1] = defaultStateWithoutSlashes;
      else {
        routesParts[routesParts.length - 1] = this.getFirstMenu();
      }
    }

    if (
      this.routesHistory.length &&
      `${this.routesHistory.join('&')}` === routesParts.join('&')
    ) {
      const url =  '/' + (window['prefix'] ? `${window['prefix']}/` : '') + this.routesHistory.join('/') + '?' + (fullLocationPath[1] || '');
      return {
        navigationUrl: url,
        queryParams: this.getQueryParamsFromQueryString(fullLocationPath[1])
      };
    }

    /// handdle full realtive here
    if (relativeRoute) {
      const route = this.findMenuByUrl(routesParts[0]);
      this.makeCall.emptyCacheOnNavigation();
      if (!keep && route && get(route, 'section') !== 'hidden') {
        this.routesHistory = [];
        this.paramsByRoute = {};
        this.persistParams = [];
        // clear all saved filters and seach terms when navigate to a new section
        this.searchFiltersService.clearAll();
      }
      const historyIdx = this.routesHistory.findIndex(
        (r) => r === routesParts[0]
      );

      if (historyIdx === -1) {
        this.routesHistory.push(...routesParts);
        this.persistParams = uniqBy([
          ...(persistParams || []),
          ...(this.persistParams || []),
        ], (param => (param || '').split('=')[0]));
        this.paramsByRoute[routesParts[routesParts.length - 1]] = uniqBy([
          ...(this.persistParams || []),
          ...(params || []),
        ], (param => (param || '').split('=')[0]));
      } else {
        let lastItemIdx = historyIdx;
        let lastRoutePartsIdx = 0;
        for(let i = 1; i < routesParts.length; i++) {
           if (this.routesHistory[historyIdx + i] === routesParts[i]){
            lastItemIdx = historyIdx + i
            lastRoutePartsIdx = i;
           } else {
              break;
           }
        }


        this.paramsByRoute = omit(
          this.paramsByRoute,
          this.routesHistory.slice(lastItemIdx + 1, this.routesHistory.length)
        );
       
        this.routesHistory = this.routesHistory.slice(0, lastItemIdx + 1).concat(lastItemIdx < routesParts.length - 1 ? routesParts.slice(lastRoutePartsIdx + 1) : []);


        this.persistParams = uniqBy([
          ...(this.persistParams || []),
          ...(persistParams || []),
        ], (param => (param || '').split('=')[0]));
        this.paramsByRoute[routesParts[routesParts.length - 1]] =    this.paramsByRoute[routesParts[routesParts.length - 1]] = uniqBy([
          ...(this.persistParams || []),
          ...(isEmpty(params)
            ? this.paramsByRoute[routesParts[routesParts.length - 1]] || []
            : params || []),
        ], (param => (param || '').split('=')[0]));

      }

    } else {
     
      const route = this.findMenuByUrl(routesParts[routesParts.length - 1]);
      // clear all cached APIs when navigate to a new component
      this.makeCall.emptyCacheOnNavigation();
      if (!keep && route && get(route, 'section') !== 'hidden') {
        this.routesHistory = [];
        this.paramsByRoute = {};
        this.persistParams = [];
        // clear all saved filters and seach terms when navigate to a new section
        this.searchFiltersService.clearAll();
      }
  
      const historyIdx = this.routesHistory.findIndex(
        (r) => r === routesParts[routesParts.length - 1]
      );
      if (historyIdx === -1) {
        this.routesHistory.push(routesParts[routesParts.length - 1]);
        this.persistParams = uniqBy([
          ...(persistParams || []),
          ...(this.persistParams || []),
        ], (param => (param || '').split('=')[0]));
        this.paramsByRoute[routesParts[routesParts.length - 1]] = uniqBy([
          ...(this.persistParams || []),
          ...(params || []),
        ], (param => (param || '').split('=')[0]));
     
      } else {
        this.paramsByRoute = omit(
          this.paramsByRoute,
          this.routesHistory.slice(historyIdx + 1, this.routesHistory.length)
        );
        this.routesHistory = this.routesHistory.slice(0, historyIdx + 1);
        this.persistParams = uniqBy([
          ...(this.persistParams || []),
          ...(persistParams || []),
        ], (param => (param || '').split('=')[0]));
        this.paramsByRoute[routesParts[routesParts.length - 1]] =    this.paramsByRoute[routesParts[routesParts.length - 1]] = uniqBy([
          ...(this.persistParams || []),
          ...(isEmpty(params)
            ? this.paramsByRoute[routesParts[routesParts.length - 1]] || []
            : params || []),
        ], (param => (param || '').split('=')[0]));
    
      }
  
    }
    this.addRouteConfig();

   
    set(this.states, 'routesState.persistParams', this.persistParams);
    set(this.states, 'routesState.paramsByRoute', this.paramsByRoute);
    const navigationHistory = this.states['routesState']?.['navigationHistory'] || [];
    const routesHistorySON = JSON.stringify(this.routesHistory);

    const isDuplicate = navigationHistory.some(existingRoutesHistor => {
      return JSON.stringify(existingRoutesHistor) === routesHistorySON;
    });

    if (!isDuplicate) {
     navigationHistory.push(this.routesHistory);
    }
    set(this.states, 'routesState.navigationHistory', navigationHistory);
    

    const queryParams = [
      ...uniq(
        (keep ? ['--keep=true'] : []).concat(this.paramsByRoute[routesParts[routesParts.length - 1]] || [])
      )
    ];

    const navigationUrl =  '/' + (window['prefix'] ? `${window['prefix']}/` : '') + this.routesHistory.join('/') + '?' + queryParams.join('&');

    return {
      navigationUrl,
      queryParams: this.getQueryParamsFromQueryString(queryParams.join('&'))
    };
  }

  get menu(): any[] {
    return window['menu'] ? (JSON.parse(window['menu']) || []) : [];
  }

  findMenuByUrl(url: string): any {
    const menuItem = (this.menu || []).find((menuItem) => {
      const routesParts = menuItem.menu_url.split('/');
      return routesParts[routesParts.length - 1] === url;
    });
    if( menuItem?.translations){
      menuItem.title = this.translationService.getDataFromTranslations(menuItem, 'title');
    }
    return menuItem;
  }

  getComponentInfo(route): any {
    if (!route) {
      return false;
    }
    return this.allAvailableRoutes.find(
      (c) => c.name === route.main_controller
    );
  }

  getQueryParamsFromQueryString(value: string) {
    const queryParams = [
      ...uniq((value || '').split('&') || []).filter(i => i)
    ];
    return queryParams.reduce((memo, param) => ({...memo, [param.split('=')[0]]: param.split('=')[1]}) , {})
  }


  calculateRouteFromMenuItem(idx, componentInfo, historyRoute, isLastRoute, parentIdx = 0): any {
    const parents = (isFunction(get(componentInfo, 'parentComponents')) ? componentInfo.parentComponents(this.routesHistory) : get(componentInfo, 'parentComponents')) || [];
    if (parents.length === parentIdx) {
      if (isLastRoute) {
        return {
          path: !parents.length ? this.routesHistory[idx] : '',
          component: componentInfo
            ? (componentInfo.module ? LazyLoadedComponent : componentInfo.component)
            : PageNotFoundComponent,
          ...(componentInfo?.canActivate ? {
            canActivate: componentInfo.canActivate
          } :  {}),  
          data: {
            title:
            /*get(historyRoute, 'translations.0.title') ||*/
            historyRoute.title,
            id: historyRoute.id,
            canonical: '/' + this.routesHistory[idx],
            icon: historyRoute.class_name,
            section: historyRoute.section,
            routeInfo: componentInfo,
            skylineBackground: true,
            breadcrumb: {
              label: historyRoute.title || this.routesHistory[idx],
              alias: this.routesHistory[idx],
            },
          }
        };
      } else {
        return {
          path: !parents.length ? this.routesHistory[idx] : '',
          children: [
            {
              path: '',
              component: componentInfo
                ? (componentInfo.module ? LazyLoadedComponent : componentInfo.component)
                : PageNotFoundComponent,
              ...(componentInfo?.canActivate ? {
                  canActivate: componentInfo.canActivate
              } :  {}),  
              data: {
                title:
                /*get(historyRoute, 'translations.0.title') ||*/
                historyRoute.title,
                id: historyRoute.id,
                canonical: '/' + this.routesHistory[idx],
                icon: historyRoute.class_name,
                section: historyRoute.section,
                routeInfo: componentInfo,
                skylineBackground: true,
                breadcrumb: {
                  label: historyRoute.title || this.routesHistory[idx],
                  alias: this.routesHistory[idx],
                },
              },
            },
            ...(!parents.length ? [this.calculateRouteFromHistory(idx + 1)] : []),
          ],
        };
      }
    } else if (parentIdx === 0) {
      return {
        path: this.routesHistory[idx],
        children: [
          {
            component: parents[parentIdx].componentName ? LazyLoadedComponent : parents[parentIdx], // parent component | module + component name
            path: '',
            ...(parents[parentIdx]?.canActivate ? {
              canActivate: parents[parentIdx].canActivate
            } :  {}),  
            data: {
              title:
              /*get(historyRoute, 'translations.0.title') ||*/
              historyRoute.title,
              id: historyRoute.id,
              canonical: '/' + this.routesHistory[idx],
              icon: historyRoute.class_name,
              section: historyRoute.section,
              routeInfo: parents[parentIdx].componentName ? parents[parentIdx] : null,
              skylineBackground: true,
              breadcrumb: {
                label: historyRoute.title || this.routesHistory[idx],
                alias: this.routesHistory[idx],
              },
            },
            children: [
              ...[this.calculateRouteFromMenuItem(idx, componentInfo, historyRoute, false, parentIdx + 1)]
            ]
          },
          ...(!isLastRoute ? [this.calculateRouteFromHistory(idx + 1)] : []),
        ]
      };
    } else {
      return {
        path: '',
        component: parents[parentIdx].componentName ? LazyLoadedComponent : parents[parentIdx],
        ...(parents[parentIdx]?.canActivate ? {
          canActivate: parents[parentIdx].canActivate
        } :  {}),  
        data: {
          routeInfo: parents[parentIdx].componentName ? parents[parentIdx] : null,
        },
        children: [
          ...[this.calculateRouteFromMenuItem(idx, componentInfo, historyRoute, false, parentIdx + 1)],
        ]
      };
    }
  }

  calculateRouteFromPredefinedConfig(idx, predefinedConfig, isLastRoute, parentIdx = 0): any {
    const parents = (isFunction(predefinedConfig.parentComponents) ? predefinedConfig.parentComponents(this.routesHistory) : predefinedConfig.parentComponents) || [];
    if (parents.length === parentIdx) {
      if (isLastRoute) {
        return {
          path: !parents.length ? predefinedConfig.path : '',
          ...omit(predefinedConfig, ['path', 'parentComponents']),
        };
      } else {
        return {
          path: !parents.length ? predefinedConfig.path : '',
          children: [
            {
              path: '',
              ...omit(predefinedConfig, ['path', 'parentComponents']),
            },
            ...((!parents.length) ? [this.calculateRouteFromHistory(idx + 1)] : []),
          ]
        };
      }
    } else if (parentIdx === 0) {
      return {
        path: predefinedConfig.path,
        children: [
          {
            path: '',
            component: parents[parentIdx].componentName ? LazyLoadedComponent : parents[parentIdx],
            ...omit(predefinedConfig, ['path', 'parentComponents', 'component', 'canActivate']),
            ...(parents[parentIdx]?.canActivate ? {
              canActivate: parents[parentIdx].canActivate
            } :  {}),  
            data: {
              ...(predefinedConfig.data || {}),
              routeInfo: parents[parentIdx].componentName ? parents[parentIdx] : null,
            },
            children: [
              ...[this.calculateRouteFromPredefinedConfig(idx, predefinedConfig, false, parentIdx + 1)],
            ]
          },
          ...(!isLastRoute ? [this.calculateRouteFromHistory(idx + 1)] : []),
        ]
      };
    } else {
      return {
        path: '',
        component: parents[parentIdx].componentName ? LazyLoadedComponent : parents[parentIdx],
        ...(parents[parentIdx]?.canActivate ? {
          canActivate: parents[parentIdx].canActivate
        } :  {}),  
        data: {
          routeInfo: parents[parentIdx].componentName ? parents[parentIdx] : null,
        },
        children: [
          ...[this.calculateRouteFromPredefinedConfig(idx, predefinedConfig, false, parentIdx + 1)],
        ]
      };
    }
  }


  calculateRouteFromHistory(idx = 0): any {
    if (idx < this.routesHistory.length - 1) {
      const historyRoute = this.findMenuByUrl(this.routesHistory[idx]);
      const componentInfo = this.getComponentInfo(historyRoute);
      const predefinedConfig = this.allPreDefinedRouteConfigs.find(
        (config) => config.path === this.routesHistory[idx]
      );

      if (historyRoute) {
        dataTranslation.breadcrumb.push({
          title: historyRoute.title || this.routesHistory[idx],
          translations: historyRoute.translations,
        });
      }
      return predefinedConfig
        ? this.calculateRouteFromPredefinedConfig(idx, predefinedConfig, false)
        : (historyRoute
          ? this.calculateRouteFromMenuItem(idx, componentInfo, historyRoute, false)
          : {
            path: this.routesHistory[idx],
            children: [
              {
                path: '',
                component: PageNotFoundComponent,
              },
              ...[this.calculateRouteFromHistory(idx + 1)],
            ],
          });
    } else {
      const route = this.findMenuByUrl(this.routesHistory[idx]);
      const componentInfo = this.getComponentInfo(route);
      const predefinedConfig = this.allPreDefinedRouteConfigs.find(
        (config) => config.path === this.routesHistory[idx]
      );
      if (route) {
        dataTranslation.breadcrumb.push({
          title: route.title || this.routesHistory[idx],
          translations: route.translations,
        });
      }
      return predefinedConfig
        ? this.calculateRouteFromPredefinedConfig(idx, predefinedConfig, true)
        : (route
          ? this.calculateRouteFromMenuItem(idx, componentInfo, route, true)
          : {
            path: this.routesHistory[idx],
            component: PageNotFoundComponent,
          });
    }
  }

  registerBeforeBackEvent(cb: () => Observable<boolean> | boolean): void {
    this.beforeBack.push(cb);
  }

  removeBeforeBackEvent(cb: () => Observable<boolean> | boolean): void {
    this.beforeBack = (this.beforeBack || []).filter(c => c !== cb);
  }

  beforeBackCall(): Observable<boolean> {
    return new Observable(observer => {
      this._beforeBackCall(0, observer);
    });
  }

  protected _beforeBackCall(idx, observer) {
    if (this.beforeBack?.length && idx < this.beforeBack.length) {
      let result = this.beforeBack[idx]();
      if (!isObservable(result)) {
        result = of(result) as Observable<boolean>;
      }
      result.subscribe(value => {
        if (value) {
          if (idx === (this.beforeBack.length -1)) {
            observer.next(true);
            observer.complete();
          } else {
            this._beforeBackCall(idx + 1, observer);
          }
        } else {
          observer.next(false);
          observer.complete();
        }
      });
    } else if (!this.beforeBack?.length) {
      observer.next(true);
      observer.complete();
    }
  }

  getSectionTitleFromMenu() {
    let sectionTitle = '';
    let parentSectionTitle = '';
    let lastPartOfUrl = (this.router.url).split('/').pop();
    if (lastPartOfUrl.includes('?')) {
      lastPartOfUrl = lastPartOfUrl.substring(0, lastPartOfUrl.indexOf('?'));
    }
    this.menu.find(item => {
      if(item?.menu_url === lastPartOfUrl) {
        if (item.menu_parent_id) {
          this.menu.find(parentItem => {
            if (parentItem.id === item.menu_parent_id) {
              parentSectionTitle = this.translationService.getDataFromTranslations(parentItem, 'title');
            }
          });
        }
        sectionTitle = this.translationService.getDataFromTranslations(item, 'title');
      }
    });
    return parentSectionTitle.length ? parentSectionTitle + ' - ' + sectionTitle : sectionTitle;
  }

  getSectionTranslationDataFromMenu(menuItem: string) {
    let sectionMenuItem = '';
    let lastPartOfUrl = (this.router.url).split('/').pop();
    if (lastPartOfUrl.includes('?')) {
      lastPartOfUrl = lastPartOfUrl.substring(0, lastPartOfUrl.indexOf('?'));
    }
    this.menu.find(item => {
      if(item?.menu_url === lastPartOfUrl) {
        if (item?.menu_parent_id) {
          sectionMenuItem = this.translationService.getDataFromTranslations(item, menuItem);
        }
      }
    });
    return sectionMenuItem;
  }

  getDataFromTranslations(item: any, key: string): any {
    return this.translationService.getDataFromTranslations(item, key);
  }

  isNavigationPeventedOnRoute(menuItem: any): boolean {
    return menuItem.type === 'service';
  }

  handleCustomRoute(menuItem: any): void {
    switch (menuItem.type) {
      case 'service': {
        const handlerConfig = this.allAvailableRoutes.find(route => route.name === menuItem.main_controller);
        if (!handlerConfig) {
          return;
        }
        this.loadService(handlerConfig).then(service => {
          service.handle(menuItem);
        });
        break;
      }
      default:
        break;
    }
  }

  async loadService(config: any): Promise<any> {
    if (config.module) {
      const cachedModule = this.lazyLoadingCacheService.getCachedModule(config.module.name);
      let result: any = null;
      let moduleRef: any = null;
      if (!cachedModule) {
        result = await config.module.load();
        moduleRef = createNgModuleRef(result.module, this.injector);
        this.lazyLoadingCacheService.cacheModule(config.module.name, result, moduleRef);
      } else {
        result = cachedModule.module;
        moduleRef = cachedModule.moduleRef;
      }
      return moduleRef.injector.get(result.components[config.serviceName || config.name]  as Type<any>);
    } else {
      return this.injector.get(config.service  as Type<any>);
    }
  }

  clearParams(){
    set(this.states, 'routesState', {});
    this.routesHistory = [];
    this.paramsByRoute = {};
    this.breadcrumbsStorage = [];
    this.persistParams = [];
  }


  initializeStateFromHistory() {
    const historyState = history.state;

    // Initialize private states from history.state
    for (const key in historyState) {
      if (historyState.hasOwnProperty(key)) {
        this.states[key] = historyState[key];
      }
    }
  
    this.persistParams = this.states['routesState']?.['persistParams'] || [];
    this.paramsByRoute =  this.states['routesState']?.['paramsByRoute'] || {};
    this.breadcrumbsStorage = this.states['routesState']?.['breadcrumbsStorage'] || [];
    this.breadcrumbsStorage.forEach(breadcrumb => {
      if (breadcrumb.alias) {
        this.breadcrumbService.set(breadcrumb.alias, breadcrumb);
      }
    });
  }

  initConfigFromNAvigationHistory() {
    const navigationHistory = this.states['routesState']?.['navigationHistory'] || [];
    const previouseRoutesHistory = cloneDeep(this.routesHistory);
    navigationHistory.forEach(routeHistory => {
      this.routesHistory = routeHistory;
      this.addRouteConfig();
    });
    this.routesHistory = previouseRoutesHistory;
  }

  getState(stateKey: string, propertyKey?: string) {
    this.initializeStateFromHistory();
    let state = this.states[stateKey];

    if (!state) {
      state = this.route.snapshot.queryParams;
      if (state) {
        this.states[stateKey] = state;
      }
    }

    if (propertyKey) {
      return this.states?.[stateKey]?.[propertyKey];
    }

    return state;
  }

  updateState(key?: string, data?: any): void {
    const params = this.route.snapshot.queryParams;
    let stateUpdates = +params['v'] || 0;
    stateUpdates = stateUpdates ? stateUpdates + 1 : 1;
    this.router.navigate([], {
      queryParams: { ...params, v: stateUpdates },
      queryParamsHandling: 'merge',
      ...(key ? {
        info: {
          stateData: { ...data },
          stateKeys: key
        },
      } : {}),
      replaceUrl: true
    });
  }

  setState(key: string, data: any) {
    this.updateState(key, data);
  }


  resetState(key: string) {
    this.states = omit(this.states, [key]);
    this.updateState();
  }

  resetAllStates() {
    this.states = pick(this.states, this.preservedStateKeys);
    this.updateState();
  }

  setParentRoutes(value: ParentRoutes | ParentRoutes[]): void {
     if (!isArray(value)) {
        value = [value];
     }
     flattenDeep(value).filter(negate(isNil)).forEach((item: ParentRoutes) => {
      this.parentRoutesByModule[item.moduleName || window['moduleName'] || 'default'] = item.menues || [];
     });
  }

  get parentRoutes(): string [] {
    const moduleName =  window['moduleName'] || 'default';
    return this.parentRoutesByModule[moduleName] || [];
  }

  goToRoute(index, info?: NavigationInfo): void {
    if (this.disableDynamicRouting) {
      return;
    }
    if (this.routesHistory.length > 1) {
      this.router.navigate([`/${this.routesHistory.slice(0, index + 1).join('/')}`], {
          queryParams: {
            '--keep': true
          },
          info: {
            ...(info || {}),
            stateKeys: info?.stateKeys || keys(this.states),
            stateData: info?.stateKeys? info?.stateData : {}
          }
        });
    }
  }

  navigateBack(info?: NavigationInfo) {
    this.beforeBackCall().subscribe(value => {
      if (value) {
        if (this.disableDynamicRouting) {
          this.staticNavigateBack(info);
        } else {
          this.dynamicNavigateBack(info);
        }
      }
    });

  }

  dynamicNavigateBack(info?: NavigationInfo) {   
    if (this.routesHistory.length > 1) {
      this.router.navigate([`/${this.routesHistory.slice(0, this.routesHistory.length - 1).join('/')}`], {
       queryParams: {
         '--keep': true
       }, 
       info: {
         ...(info || {}),
         stateKeys: info?.stateKeys || keys(this.states),
         stateData: info?.stateKeys? info?.stateData : {}
       }
      });
    } else {
      this.tokenStorage.getConfiguration().subscribe(res => {
          const defaultState = get(res,'default_state') || '/home';
          this.router.navigate([defaultState]);
      });
    }
  }

  staticNavigateBack(info?: NavigationInfo): void {
    const urlSegments = this.router.url.split('/');
    urlSegments.pop();
    const newUrl = urlSegments.join('/');
    this.router.navigate([newUrl], {
      info: {
        info: {
          ...(info || {}),
          stateKeys: info?.stateKeys || keys(this.states),
          stateData: info?.stateKeys? info?.stateData : {}
        }
      }
    });
  }

  addRouteConfig() {
    
    let isRouteAdded = false;
    const routerRoutesFinal = this.router.config;
    routerRoutesFinal.forEach((element) => {
      if (isRouteAdded) {
        return;
      }
      if (element.path === (window['prefix'] || '') && element.children) {
        element.children.forEach((r) => {
          if (isRouteAdded) {
            return;
          }
          if (r.path == '') {
            if (!this.routeChildren) {
              this.routeChildren = r.children;
            }
            dataTranslation.breadcrumb
              = [];
            r.children = this.mergeChildren(r.children || []);
            isRouteAdded = true;
          }
        });
      }
    });
    /***
     * Closing as a "won't fix". resetConfig is a config reset and should be assumed to be treated as a completely new config. As such, the previously stored routes cannot be re-used. That said, you can directly access and modify the router.config so that the object equality still works. https://angular.io/api/router/Router#config
     * https://github.com/angular/angular/issues/38326
     */
    // this.router.resetConfig(routerRoutesFinal);
  }

  get disableDynamicRouting(): boolean {
    const generalConfig = this.injector.get(CommonGeneralConfigService);
    return generalConfig.getConfigProperty('commonConfig.disableDynamicRouting');
  }
}
 



